import { AppBarAppsList } from "./app-bar-components/app-bar-lists/app-bar-apps-list.component";
import { SidebarListsContainer } from "./app-bar-components/app-bar-lists/sidebar-lists-container.component";
import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { useCentralStore } from "../../../store/Central";
import { useAttributesStore } from "../../../store/Attributes/attributes.store";
import { SideNavigation } from "../../../../../components/src/side-navigation/SideNavigation";
import { DlOrgHeaderButton } from "./DlOrgHeaderButton";
import { getLocalStorage, setLocalStorage } from "@/lib/utils/local-storage";
import { PageSidebarItemWithChatPreview } from "./app-bar-components/PageSidebarItemWithChatPreview";
import { getAppListMenuItems } from "./app-bar-components/app-bar-lists/get-app-list-menu-items.function";
import {
	ORG_SUBPAGE_FAVORITE_MAP,
	SidebarFavoritesDialog,
} from "./app-bar-components/sidebar-favorites-dialog.component";
import { hasFullFeatureSet } from "@/dentlab/src/store/Central/selectors";
import { useUploadGuard } from "@/dentlab/src/hooks/useUploadGuard";
import { FeatureGateModal } from "@/dentlab/src/components/subscription/feature-gate-modal.component";
import { SidebarExtensionPanel } from "./app-bar-components/sidebar-extension-panel.component";
import { DesktopUpdateBanner } from "./app-bar-components/desktop-update-banner.component";

export function hasConfigValue(
	config: any,
	option: string,
	value: any
): boolean {
	return (
		config &&
		typeof config === "object" &&
		option in config &&
		config[option] === value
	);
}

const SIDEBAR_FAV_PREFIX = "sidebar-fav-";
const SIDEBAR_HIDE_PREFIX = "sidebar-hide-";

export const DlSideNavigation: React.FC<{
	className?: string;
}> = ({ className }) => {
	const [isPinned, setIsPinned] = useState(() =>
		getLocalStorage("dentlab-sidebar-pinned", true)
	);

	const [isExpanded, setIsExpanded] = useState(isPinned);
	const [isClientsCollapsed, setIsClientsCollapsed] = useState(() =>
		getLocalStorage("dentlab-sidebar-clients-collapsed", false)
	);
	const [isVendorsCollapsed, setIsVendorsCollapsed] = useState(() =>
		getLocalStorage("dentlab-sidebar-vendors-collapsed", false)
	);
	const [recentClientInteraction, setRecentClientInteraction] =
		useState(false);
	const [favDialogOpen, setFavDialogOpen] = useState(false);
	const [extensionPanel, setExtensionPanel] = useState<{
		type: "clients" | "vendors";
	} | null>(null);
	const timeoutRef = useRef<NodeJS.Timeout | null>(null);

	const { organization } = useCentralStore((state) => ({
		organization: state.organization,
	}));
	const role = useCentralStore((state) => state.role ?? 2);
	const subscriptionTier = useCentralStore((state) => state.subscriptionTier);
	const attributes = useAttributesStore((state) => state.attributes);
	const checkNavigationAllowed = useUploadGuard();
	const [showGateModal, setShowGateModal] = useState(false);

	// Sync expanded state with pinned state on mount
	useEffect(() => {
		if (isPinned) {
			setIsExpanded(true);
			document.body.classList.add("sidebar-expanded");
		}

		return () => {
			document.body.classList.remove("sidebar-expanded");
			if (timeoutRef.current) {
				clearTimeout(timeoutRef.current);
			}
		};
	}, []);

	useEffect(() => {
		if (!extensionPanel) return;

		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key === "Escape") setExtensionPanel(null);
		};

		const handleClickOutside = (e: MouseEvent) => {
			const sidebar = document.querySelector(".side-navigation-overlay");
			if (sidebar && !sidebar.contains(e.target as Node)) {
				setExtensionPanel(null);
			}
		};

		document.addEventListener("keydown", handleKeyDown);
		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("keydown", handleKeyDown);
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [extensionPanel]);

	const handleMouseEnter = () => {
		if (!isPinned && !recentClientInteraction) {
			setIsExpanded(true);
		}
	};

	const handleMouseLeave = () => {
		if (!isPinned) {
			setIsExpanded(false);
			setExtensionPanel(null);
		}
	};

	const handleClientInteraction = () => {
		setRecentClientInteraction(true);

		if (timeoutRef.current) {
			clearTimeout(timeoutRef.current);
		}

		timeoutRef.current = setTimeout(() => {
			setRecentClientInteraction(false);
		}, 300);

		handleMouseLeave();
	};

	const handleExtensionItemClick = () => {
		setExtensionPanel(null);
		handleClientInteraction();
	};

	const handleOverflowClick = useCallback((type: "clients" | "vendors") => {
		setExtensionPanel((prev) => (prev?.type === type ? null : { type }));
	}, []);

	const togglePin = () => {
		const newPinnedState = !isPinned;
		setIsPinned(newPinnedState);
		setLocalStorage("dentlab-sidebar-pinned", newPinnedState);
		setIsExpanded(newPinnedState);
		if (!newPinnedState) setExtensionPanel(null);
	};

	const orgFirstLetter = (organization?.name || "L").charAt(0).toUpperCase();
	const orgName = organization?.name || "Labor";

	const config = useCentralStore((state) => state.organization?.config);
	const shouldShowCode = hasConfigValue(config, "sidebar_type", "codes");

	const hasFull = hasFullFeatureSet(subscriptionTier);
	const allNavItems = useMemo(() => getAppListMenuItems(role), [role]);

	const favoriteKeys = useMemo(() => {
		const keys: string[] = [];
		for (const key of Object.keys(attributes)) {
			if (key.startsWith(SIDEBAR_FAV_PREFIX) && attributes[key] === 1) {
				keys.push(key.slice(SIDEBAR_FAV_PREFIX.length));
			}
		}
		return keys;
	}, [attributes]);

	const hiddenKeys = useMemo(() => {
		const keys: string[] = [];
		for (const key of Object.keys(attributes)) {
			if (key.startsWith(SIDEBAR_HIDE_PREFIX) && attributes[key] === 1) {
				keys.push(key.slice(SIDEBAR_HIDE_PREFIX.length));
			}
		}
		return keys;
	}, [attributes]);

	const hideVendors = attributes["sidebar-hide-vendors"] === 1;

	const favoriteItems = useMemo(() => {
		const navItemsByKey = new Map(
			allNavItems.map((item) => [item.key, item])
		);
		const items: Array<{
			key: string;
			title: string;
			path: string;
			icon: React.ReactNode;
			isGated: boolean;
			isOrgSubpage: boolean;
		}> = [];

		const orderedNavKeys = allNavItems.map((i) => i.key);
		const orderedOrgKeys = Object.keys(ORG_SUBPAGE_FAVORITE_MAP);
		const allOrderedKeys = [...orderedNavKeys, ...orderedOrgKeys];

		for (const key of allOrderedKeys) {
			if (!favoriteKeys.includes(key)) continue;

			const navItem = navItemsByKey.get(key);
			if (navItem) {
				if (navItem.isGated && !hasFull) continue;
				items.push({ ...navItem, isOrgSubpage: false });
				continue;
			}

			const orgItem = ORG_SUBPAGE_FAVORITE_MAP[key];
			if (orgItem) {
				items.push({
					key,
					title: orgItem.title,
					path: orgItem.path,
					icon: orgItem.icon,
					isGated: false,
					isOrgSubpage: true,
				});
			}
		}

		return items;
	}, [allNavItems, favoriteKeys, hasFull]);

	const excludeKeys = useMemo(
		() => favoriteItems.filter((i) => !i.isOrgSubpage).map((i) => i.key),
		[favoriteItems]
	);

	const expanded = isExpanded || isPinned;
	const location = useLocation();

	const suppressedKeys = useMemo(() => {
		const visibleOrgItems = Object.entries(ORG_SUBPAGE_FAVORITE_MAP)
			.filter(
				([key]) =>
					attributes[`sidebar-fav-${key}`] === 1 ||
					attributes[`sidebar-show-${key}`] === 1
			)
			.map(([key, item]) => ({ key, path: item.path }));
		const allItems = [
			...allNavItems.map((i) => ({ key: i.key, path: i.path })),
			...visibleOrgItems,
		];
		const activeItems = allItems.filter(
			(item) =>
				location.pathname === item.path ||
				location.pathname.startsWith(item.path + "/")
		);
		const keys = new Set<string>();
		for (const item of activeItems) {
			for (const other of activeItems) {
				if (
					other.path !== item.path &&
					other.path.startsWith(item.path)
				) {
					keys.add(item.key);
					break;
				}
			}
		}
		return keys;
	}, [allNavItems, attributes, location.pathname]);

	const handleGearClick = useCallback(() => setFavDialogOpen(true), []);

	return (
		<SideNavigation
			isExpanded={isExpanded}
			isPinned={isPinned}
			handleMouseEnter={handleMouseEnter}
			handleMouseLeave={handleMouseLeave}
			togglePin={togglePin}
			className={className}
			topLeftMenu={
				<DlOrgHeaderButton
					orgName={orgName}
					orgFirstLetter={orgFirstLetter}
					isExpanded={expanded}
					isPinned={isPinned}
				/>
			}
		>
			{/* Favorites section */}
			{favoriteItems.length > 0 && (
				<div className="flex-shrink-0 flex flex-col gap-0.5 w-full">
					{favoriteItems.map((item) => (
						<div
							key={item.key}
							data-testid={`sidebar-fav-item-${item.key}`}
						>
							<PageSidebarItemWithChatPreview
								itemKey={item.key}
								title={item.title}
								path={item.path}
								icon={item.icon}
								expanded={expanded}
								onChatInteraction={
									item.key === "chats"
										? handleClientInteraction
										: undefined
								}
								onAppInteraction={handleClientInteraction}
								onBeforeNavigate={checkNavigationAllowed}
								isLocked={item.isGated && !hasFull}
								onLockedClick={() => setShowGateModal(true)}
								suppressHighlight={suppressedKeys.has(item.key)}
							/>
						</div>
					))}
					<hr className="border-stone-200 mx-2 my-1" />
				</div>
			)}

			<SidebarListsContainer
				expanded={expanded}
				showCode={shouldShowCode}
				isClientsCollapsed={isClientsCollapsed}
				isVendorsCollapsed={isVendorsCollapsed}
				hideVendors={hideVendors}
				onClientInteraction={handleClientInteraction}
				onClientsCollapseChange={setIsClientsCollapsed}
				onVendorsCollapseChange={setIsVendorsCollapsed}
				onOverflowClick={handleOverflowClick}
			/>

			<DesktopUpdateBanner expanded={expanded} />

			{/* Apps list - fixed at bottom */}
			<div className="flex-shrink-0">
				<AppBarAppsList
					expanded={expanded}
					excludeKeys={excludeKeys}
					hiddenKeys={hiddenKeys}
					suppressedKeys={suppressedKeys}
					onChatInteraction={handleClientInteraction}
					onAppInteraction={handleClientInteraction}
					onGearClick={handleGearClick}
				/>
			</div>

			<FeatureGateModal
				open={showGateModal}
				onOpenChange={setShowGateModal}
			/>

			<SidebarFavoritesDialog
				open={favDialogOpen}
				onOpenChange={setFavDialogOpen}
			/>

			{extensionPanel && (
				<SidebarExtensionPanel
					type={extensionPanel.type}
					onClose={() => setExtensionPanel(null)}
					onItemClick={handleExtensionItemClick}
				/>
			)}
		</SideNavigation>
	);
};
